// File: frontend/src/services/api.js
import axios from "axios";
import { handleSessionExpired, isAuthEndpoint } from "../utils/authNotifications";
import { clearAccountScope, bumpAuthEpoch, getAuthEpoch, isCurrentEpoch } from "../utils/accountScope";
import { stampFetched } from "../utils/routerStatus";
import { API_BASE_URL, API_URL } from "../config/apiBase";

/**
 * CONFIGURATION
 * API base URL is resolved centrally in ../config/apiBase:
 *   • REACT_APP_API_URL when set (production: https://api.pesafii.net)
 *   • never the retired DuckDNS domain
 *   • http://localhost:5000 in development
 */

// Central axios instance
//
// TIMEOUT IS NOT OPTIONAL.
//
// axios defaults to `timeout: 0` — wait forever. A request issued as a phone's
// radio hands over can then sit unresolved indefinitely: it never resolves and
// never rejects. Anything awaiting it waits for the life of the tab, and any
// promise-sharing layer built on top of it (see routerService below) stays
// pinned to that dead promise. Measured consequence: the sidebar stopped
// refreshing router presence until a full page reload.
//
// 60 s is far beyond any ordinary read on this API and still bounds the
// pathological case. It is a ceiling on the unexpected, not a latency budget.
const REQUEST_TIMEOUT_MS = 60000;

// ── THE CALLS THAT ARE GENUINELY LONG ────────────────────────────────────────
// Four endpoints legitimately run past a minute and must NOT inherit the
// default: /routers/:id/setup-script (registers a WireGuard peer and renders
// the portal), /prepare-onboarding, /provision, and the customer-migration
// import — which walks a batch synchronously, and batches reach 7,819 records
// in production. Capping those at a read's latency would turn working
// provisioning and a working migration into timeouts, which is a worse bug than
// the one being fixed.
//
// They are bounded, just generously. A finite 5 minutes still means a dead
// socket eventually surfaces as an error the operator can act on, instead of a
// spinner that never resolves. These are foreground, user-initiated, one-shot
// operations with visible UI — quite unlike the background poller whose silent
// wedge caused this work.
const LONG_RUNNING = { timeout: 300000 };

const api = axios.create({
  baseURL: API_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { "Content-Type": "application/json" },
});

const TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_KEY = "user";
const DEVICE_ID_KEY = "device_id";

function getDeviceId() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    const randomPart =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    deviceId = `web-${randomPart}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

let refreshPromise = null;

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/users/refresh`, {
        refresh_token: refreshToken,
        device_id: getDeviceId(),
      })
      .then(({ data }) => {
        if (!data?.success || !data?.token) return null;
        localStorage.setItem(TOKEN_KEY, data.token);
        if (data.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        return data.token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

/**
 * REQUEST INTERCEPTOR
 * Attach JWT token automatically
 */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    config.__hadAuthToken = Boolean(token);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    config.headers["X-Device-Id"] = getDeviceId();
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * RESPONSE INTERCEPTOR
 * Auto logout on 401
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const hadAuthToken = error?.config?.__hadAuthToken;
    const requestUrl = error?.config?.url || "";

    // ── A TRANSACTION PIN IS NOT THE SESSION ─────────────────────────────────
    // The wallet PIN authorises one money movement; it says nothing about
    // whether the login is still valid. The server used to answer a wrong PIN
    // with 401, which landed here and signed the operator out — so forgetting a
    // PIN cost them their session. The server now answers 403, and this is the
    // second lock on that door: whatever status a wallet-PIN refusal arrives
    // with, it must never tear down the account scope.
    const pinCode = error?.response?.data?.code || "";
    const isWalletPinRefusal = /^(WALLET_)?PIN_(INVALID|FORMAT|LOCKED|NOT_SET)$/.test(pinCode);

    if (error?.response?.status === 401 && hadAuthToken
        && !isAuthEndpoint(requestUrl) && !isWalletPinRefusal) {
      if (!error.config.__isRetryRequest) {
        const freshToken = await refreshAccessToken();
        if (freshToken) {
          error.config.__isRetryRequest = true;
          error.config.headers = error.config.headers || {};
          error.config.headers.Authorization = `Bearer ${freshToken}`;
          return api(error.config);
        }
      }

      // Session invalidation is an account change too: bump the epoch and tear
      // down the whole account scope, not just the three credential keys.
      bumpAuthEpoch();
      clearAccountScope();
      handleSessionExpired({
        storageKeys: [TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY],
        loginPath: "/login",
      });
    }
    return Promise.reject(error);
  }
);

/* =========================
   AUTH SERVICES
========================= */
// Notify in-app listeners (e.g. PermissionsProvider) that the admin auth state
// changed, so they refetch WITHOUT needing a full page reload. Login navigates
// via React Router (SPA), so the already-mounted providers never remount on
// their own — this event is how they learn to reload /me + /me/permissions.
function emitAuthChanged() {
  try { window.dispatchEvent(new Event("pesafii:auth-changed")); } catch (_) {}
}

export const authService = {
  /**
   * Persist a successful authentication.
   *
   * Every login method ends here — password, set-password, and Google — for the
   * same reason the backend funnels them all through one session issuer: a
   * second copy of "which keys hold the session" is how a login path ends up
   * storing the token but not the refresh token, or forgetting to bump the auth
   * epoch (which is what invalidates the previous account's cached data).
   */
  adoptSession: (data) => {
    if (!data?.success || !data?.token) return data;
    localStorage.setItem(TOKEN_KEY, data.token);
    if (data.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    bumpAuthEpoch();
    emitAuthChanged();
    return data;
  },

  getDeviceId,

  // ✅ Login: keep old signature (username, password)
  login: async (username, password) => {
    const { data } = await api.post("/users/login", { username, password, device_id: getDeviceId() });
    return authService.adoptSession(data);
  },

  // ✅ Step 1: Start registration (email REQUIRED, phone REQUIRED)
  // `referral` is optional and stays optional all the way down: an unknown or
  // unusable code is reported in the 201 response and NEVER refuses the
  // registration. A signup lost to a mistyped code is a customer lost.
  registerStart: async (username, phone, email, referral = {}) => {
    const { data } = await api.post("/users/register-start", {
      username,
      phone,
      email,
      referral_ticket: referral.ticket || undefined,
      referral_code: referral.code || undefined,
    });
    return data;
  },

  resendVerification: async (email) => {
    const { data } = await api.post("/users/resend-verification", { email });
    return data;
  },

  // ✅ Step 2: Verify email code
  verifyEmailCode: async (email, code) => {
    const { data } = await api.post("/users/verify-email", { email, code });
    return data;
  },

  // ✅ Step 3: Set password (after verification)
  setPassword: async (email, code, password) => {
    const { data } = await api.post("/users/set-password", { email, code, password });
    return authService.adoptSession(data);
  },

  // The ONE logout. Every call site must use this and nothing else — a caller
  // that clears only part of the account scope is how the cross-account leak
  // happened (Topbar cleared the credentials, Sidebar also cleared the router,
  // and neither touched the cached dashboard payload).
  logout: () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      axios.post(`${API_URL}/users/logout`, { refresh_token: refreshToken }).catch(() => {});
    }
    // Invalidate first: any in-flight response now belongs to a past epoch and
    // will be discarded rather than written into the next account's state.
    bumpAuthEpoch();
    clearAccountScope();
    emitAuthChanged();
  },

  getCurrentUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  /**
   * Re-issue the access token from the refresh token, in place. The refresh
   * endpoint re-reads users.tenant_id, so this is how a session picks up a
   * business created a moment ago without signing out and back in.
   */
  refreshSession: async () => {
    const token = await refreshAccessToken();
    if (token) { bumpAuthEpoch(); emitAuthChanged(); }
    return token;
  },

  /**
   * The ONLY logout entry point for UI components.
   *
   * Tears down the account scope and then leaves via a FULL DOCUMENT LOAD, not
   * an SPA navigation. The hard navigation is defence in depth, not the fix: it
   * guarantees every mounted tenant page is destroyed and every in-flight
   * request is abandoned at exactly the moment the credentials go away. The
   * cache namespacing and clearAccountScope() above remain mandatory.
   */
  logoutAndRedirect: (loginPath = "/login") => {
    authService.logout();
    try { window.location.assign(loginPath); }
    catch (_) { window.location.href = loginPath; }
  },
};

/**
 * One-time sign-in handoff between pesafii.net and ambassador.pesafii.net.
 * See services/handoff.js (issuing) and auth/HandoffPage.js (redeeming).
 *
 * `redeem` deliberately uses bare axios, not the `api` instance: the
 * destination may already hold a DIFFERENT person's session, and that person's
 * token must not ride on the request, nor its 401 handling fire.
 */
export const authHandoffService = {
  issue: async (destination, returnPath) => {
    const { data } = await api.post("/auth/handoff", { destination, return_path: returnPath });
    return data;
  },
  redeem: async (code, existingUserId = null) => {
    const { data } = await axios.post(`${API_URL}/auth/handoff/redeem`, {
      code, device_id: getDeviceId(), existing_user_id: existingUserId,
    });
    return data;
  },
  /** Audit only: the person chose to switch accounts on the destination. */
  recordAccountSwitch: async (fromUserId) => {
    const { data } = await api.post("/auth/handoff/account-switch", { from_user_id: fromUserId });
    return data;
  },
  /** Revoke a session by its refresh token without touching this origin's own. */
  discardSession: (refreshToken) => {
    if (!refreshToken) return Promise.resolve();
    return axios.post(`${API_URL}/users/logout`, { refresh_token: refreshToken }).catch(() => {});
  },
};

/**
 * The Ambassador's withdrawal PIN. Person-scoped: it needs no business. The
 * scope is (the person's tenant, or none) + the user — exactly the scope Ambassador
 * payouts verify — and set/change/reset step up with the account's OTP/TOTP
 * factor, like the business wallet PIN. Responses mirror /wallet/pin/*:
 * `{ requires_verification: true, verification_method, otp_sent_to, message }`
 * when a code has just been sent.
 */
export const ambassadorPinService = {
  status: async () => (await api.get("/wallet/ambassador/pin/status")).data,
  set: async (body) => (await api.post("/wallet/ambassador/pin/set", body)).data,
  change: async (body) => (await api.post("/wallet/ambassador/pin/change", body)).data,
  reset: async (body) => (await api.post("/wallet/ambassador/pin/reset", body)).data,
};

/* =========================
   ROUTER SERVICES
========================= */
export const routerService = {
  // ── Core CRUD ─────────────────────────────────────────────────────────────

  // ── IN-FLIGHT DEDUPLICATION (not a cache) ──────────────────────────────────
  // Measured in production: /api/routers went out TWICE on every warm dashboard
  // load, in 7 of 7 samples. Two components mount at the same moment and each
  // asks independently — Sidebar.js (which also polls every 30 s) and
  // Dashboard.js — and ten further pages ask again when they mount. Neither
  // knows the other exists.
  //
  // This shares the ANSWER TO A QUESTION ALREADY IN THE AIR, and nothing more.
  // The promise is held only while the request is open and is dropped the moment
  // it settles, so a later caller always triggers a fresh request. That is the
  // deliberate difference from a TTL cache: no caller can ever be handed a
  // router list that finished loading before it asked.
  //
  // Two ways out of the shared path, both mandatory:
  //   • a caller passing its own AbortSignal is opting into cancellation, and
  //     sharing one promise between callers would let whoever aborts first
  //     cancel everyone else's request;
  //   • the auth epoch is captured when the flight starts and re-checked when it
  //     lands, so a list fetched for account A can never be delivered to account
  //     B after a switch. bumpAuthEpoch() runs on login, logout and 401.
  // ── SHORT-LIVED RESULT CACHE (Phase 12) ────────────────────────────────────
  // In-flight sharing above only helps callers that overlap. Production showed
  // the two dashboard fetches land roughly 500 ms APART — Sidebar mounts, then
  // Dashboard — so the first has already settled and the promise has been
  // dropped by the time the second asks. Deduplication could never catch them.
  //
  // So the settled answer is held for a few SECONDS. Long enough to collapse one
  // navigation's duplicate fetches; far too short to be stale router state that
  // anyone could mistake for a source of truth.
  //
  // The same epoch guard as the in-flight path applies, so a list fetched for
  // account A can never be served to account B — bumpAuthEpoch() runs on login,
  // logout and 401. Only SUCCESSES are stored; a failed request must never be
  // remembered. Mutations call invalidateRouters() explicitly.
  _routersCache: null,
  _routersTtlMs: 5000,

  // Presence must fail fast or it lies. This list is served from stored state
  // behind a Redis cache and answers in tens of milliseconds; anything past a
  // few seconds is a broken path, not a slow one, and the caller needs to LEARN
  // that so it can fall back to "unknown" rather than keep showing a confident
  // stale answer. Tighter than the instance default for that reason alone.
  _routersTimeoutMs: 12000,

  /* Drop the cached list immediately. Called by every mutation that changes what
     /routers returns, so a rename or decommission is never hidden behind a few
     seconds of staleness. Login, logout and 401 are already covered by the auth
     epoch, which invalidates this cache without needing to clear it. */
  invalidateRouters: () => { routerService._routersCache = null; },
  getRouters: async (adminId, config = {}) => {
    // An AbortSignal must not opt a caller out of the CACHE — only out of
    // JOINING someone else's in-flight request.
    //
    // Measured in production 15 Aug 2026: /api/routers ran p50 = 2 calls per
    // admin page load (55% of loads duplicated it). The pages that passed a
    // signal — Routers.js and Vouchers.js, doing the ordinary React cleanup
    // thing — were excluded from the cache in BOTH directions, so they neither
    // read the Sidebar's freshly cached list nor warmed it for anyone else.
    //
    // Reading an already-settled cache entry is safe with any signal: there is
    // no shared request for an abort to cancel. Writing is safe too — the data
    // is valid once it has resolved, regardless of who asked. Only sharing an
    // in-flight promise stays restricted, because aborting one caller must
    // never cancel the request another caller is waiting on.
    const joinable = !config.signal;
    const key = adminId ? `admin:${adminId}` : "self";

    const hit = routerService._routersCache;
    if (
      hit && hit.key === key && isCurrentEpoch(hit.epoch) &&
      Date.now() - hit.at < routerService._routersTtlMs
    ) {
      return hit.data;
    }

    const flight = routerService._routersInflight;
    if (joinable && flight && flight.key === key && isCurrentEpoch(flight.epoch)) {
      return flight.promise;
    }
    const epoch = getAuthEpoch();
    const promise = api
      .get(`/routers${adminId ? `?admin_id=${adminId}` : ""}`, {
        // A caller's own timeout still wins; otherwise presence fails fast.
        timeout: routerService._routersTimeoutMs,
        ...config,
      })
      .then(({ data }) => {
        // Belongs to a previous account — refuse it rather than hand it over.
        if (!isCurrentEpoch(epoch)) {
          const e = new Error("Router list belongs to a previous session");
          e.code = "STALE_ACCOUNT";
          throw e;
        }
        // ── STAMP THE MOMENT WE RECEIVED IT ──────────────────────────────────
        // Stamped HERE, once, rather than in each of the eight pages that store
        // this list — a page that forgot the stamp could not tell "this router
        // is stale" from "my data is stale", and those two rendered identically.
        // That is the defect this whole change exists to remove, so the stamp
        // belongs at the single point every caller already funnels through.
        //
        // The row-level copy also carries presence_thresholds down from the
        // envelope, so a router object stays self-describing after a page pulls
        // it out of `data.routers`.
        const stamped = Array.isArray(data?.routers)
          ? { ...data, routers: stampFetched(data.routers, data) }
          : data;

        // Success only. A rejected promise never reaches here, so a failed
        // request cannot be cached and re-served.
        // Always populate: a signal-carrying caller produced valid data too, and
        // refusing to store it is what kept the Routers and Vouchers pages
        // re-fetching a list the Sidebar had just loaded.
        routerService._routersCache = { key, epoch, at: Date.now(), data: stamped };
        return stamped;
      })
      .finally(() => {
        if (routerService._routersInflight && routerService._routersInflight.promise === promise) {
          routerService._routersInflight = null;
        }
      });
    // ── THE SHARED PROMISE MUST BE RELEASABLE ────────────────────────────────
    // `.finally()` only runs when the promise SETTLES. With no timeout that was
    // not guaranteed, so one hung request pinned this entry for the life of the
    // tab and every later poll joined the same dead promise — the sidebar then
    // never refreshed again until a full page load. The per-request timeout
    // above is the real fix (a timed-out request rejects, so .finally runs);
    // this watchdog is the second lock on that door, because an entry that can
    // only be cleared by a settlement is an entry that can be pinned forever.
    if (joinable) {
      const entry = { key, epoch, promise };
      routerService._routersInflight = entry;
      setTimeout(() => {
        if (routerService._routersInflight === entry) routerService._routersInflight = null;
      }, routerService._routersTimeoutMs + 1000);
    }
    return promise;
  },

  // ── Persisted selected router (canonical: users.last_selected_router_id) ────
  // Cross-device source of truth for the sidebar selection. localStorage is a
  // same-device cache/fallback; these keep the backend column in sync.
  getLastSelectedRouter: async () => {
    const { data } = await api.get("/routers/last-selected");
    return data; // { success, router_id }
  },
  setLastSelectedRouter: async (routerId) => {
    const { data } = await api.put("/routers/last-selected", { router_id: routerId ?? null });
    return data;
  },

  getRouter: async (id) => {
    const { data } = await api.get(`/routers/${id}`);
    return data;
  },

  addRouter: async (routerData) => {
    const { data } = await api.post("/routers", routerData);
    routerService.invalidateRouters();
    return data;
  },

  // Undo a decommission: brings the existing router row back with its sales
  // history and packages attached (packages stay disabled — see the route).
  restoreRouter: async (id, payload = {}) => {
    const { data } = await api.post(`/routers/${id}/restore`, payload);
    routerService.invalidateRouters();
    return data;
  },

  updateRouter: async (id, routerData) => {
    const { data } = await api.put(`/routers/${id}`, routerData);
    routerService.invalidateRouters();
    return data;
  },

  // ── Danger Zone: OTP-guarded delete + transfer ─────────────────────────────

  // Request a single-use OTP (emailed to the tenant owner) for a Danger-Zone
  // action. action ∈ "delete" | "transfer". OTP is never returned here.
  requestRouterDangerOtp: async (routerId, action) => {
    const { data } = await api.post(`/routers/${routerId}/danger-otp`, { action });
    return data;
  },

  // Decommission (safe soft-delete) a router. payload: { confirm_name, otp, reason? }
  // Internet sharing protection — reads and writes the ROUTER, never just the
  // database. GET returns enforcing=null when the router cannot be reached, so
  // a toggle can say "unknown" instead of guessing.
  getSharingProtection: async (routerId) => {
    const { data } = await api.get(`/routers/${routerId}/sharing-protection`);
    return data;
  },
  setSharingProtection: async (routerId, enabled) => {
    const { data } = await api.post(`/routers/${routerId}/sharing-protection`, { enabled: !!enabled }, LONG_RUNNING);
    return data;
  },
  deleteRouter: async (routerId, payload = {}) => {
    const { data } = await api.post(`/routers/${routerId}/decommission`, payload);
    routerService.invalidateRouters();
    return data;
  },

  // Transfer a router to another tenant/admin.
  // payload: { target_admin_email, confirm_name, otp }
  transferRouter: async (routerId, payload = {}) => {
    const { data } = await api.post(`/routers/${routerId}/transfer`, payload);
    routerService.invalidateRouters();
    return data;
  },

  // ── Analytics ─────────────────────────────────────────────────────────────

  getAnalytics: async (routerId, from, to) => {
    const params = {};
    if (from) params.from = from;
    if (to)   params.to   = to;
    const { data } = await api.get(`/routers/${routerId}/analytics`, { params });
    return data;
  },

  // Real historical observability (CPU / memory / traffic / active users) for one
  // router, downsampled server-side. `range` ∈ 15m,30m,1h,4h,12h,1d,1w,1mo,1y.
  // Pass an AbortController signal via config to cancel stale requests.
  getObservability: async (routerId, range = "1h", config = {}) => {
    const { data } = await api.get(`/routers/${routerId}/observability`, {
      ...config,
      params: { range },
    });
    return data;
  },

  // The same stored series over a BUSINESS-DAY window that opens at `from`
  // (YYYY-MM-DD, business timezone) and closes now — so the Router Console's
  // Traffic chart covers exactly the period its analytics cards read.
  getObservabilityWindow: async (routerId, { from }, config = {}) => {
    const { data } = await api.get(`/routers/${routerId}/observability`, {
      ...config,
      params: { from },
    });
    return data;
  },

  // The router's wiring (bridge membership, uplink evidence, hotspot interface),
  // read from the DEVICE on demand and redacted server-side. Part of Live check;
  // never polled.
  readWiringSnapshot: async (routerId) => {
    const { data } = await api.post(`/routers/${routerId}/wiring-snapshot`, {}, LONG_RUNNING);
    return data;
  },

  // This router's data usage from RADIUS accounting, by business month.
  // `available:false` means it cannot be measured — never read it as zero.
  getLifetimeUsage: async (routerId, config = {}) => {
    const { data } = await api.get(`/routers/${routerId}/lifetime-usage`, config);
    return data;
  },

  // ── Heartbeat (internal/admin use) ────────────────────────────────────────

  heartbeat: async (routerId, payload = {}) => {
    const { data } = await api.post(`/routers/${routerId}/heartbeat`, payload);
    return data;
  },

  // ── Setup script generation ────────────────────────────────────────────────

  getSetupScript: async (routerId, payload) => {
    const { data } = await api.post(`/routers/${routerId}/setup-script`, payload || {}, LONG_RUNNING);
    return data;
  },

  // ── Prepare onboarding ─────────────────────────────────────────────────────

  prepareOnboarding: async (routerId) => {
    const { data } = await api.post(`/routers/${routerId}/prepare-onboarding`, {}, LONG_RUNNING);
    return data;
  },

  // ── Migration-safe provisioning (adoption) ─────────────────────────────────
  // Queues the ensure-only PROVISION_ROUTER job: RADIUS client, hotspot profile,
  // walled garden, fasttrack. Idempotent and non-destructive (never touches
  // SSID/bridge/DHCP or live sessions), so it is safe on a router still serving
  // another platform. provisioning_state: new → provisioning → ready.
  provisionRouter: async (routerId, payload = {}) => {
    const { data } = await api.post(`/routers/${routerId}/provision`, payload, LONG_RUNNING);
    return data;
  },

  // ── Router secrets ─────────────────────────────────────────────────────────

  updateSecrets: async (routerId, secrets) => {
    const { data } = await api.post(`/routers/${routerId}/secrets`, secrets);
    return data;
  },

  // ── RouterOS REST proxy ────────────────────────────────────────────────────

  proxyRequest: async (routerId, { method = "GET", path, query, body } = {}) => {
    const { data } = await api.post(`/router-proxy/${routerId}/request`, {
      method,
      path,
      query,
      body,
    });
    return data;
  },

  // ── Top data users ─────────────────────────────────────────────────────────

  // `period` { from, to } (YYYY-MM-DD business days) scopes the aggregate to
  // sessions STARTED in that range; omitted, the answer is all time.
  getTopDataUsers: async (routerId, limit = 10, period = {}) => {
    const params = { limit };
    if (period.from) params.from = period.from;
    if (period.to) params.to = period.to;
    const { data } = await api.get(`/routers/${routerId}/top-users`, { params });
    return data;
  },

  // ── Router status health check ─────────────────────────────────────────────

  getHealthReport: async (routerId, params = {}) => {
    const { data } = await api.get(`/router-status/${routerId}/report`, { params });
    return data;
  },

  getHealthCheck: async (routerId) => {
    const { data } = await api.get(`/router-status/${routerId}/health`);
    return data;
  },

  // ── Mark stale routers offline ─────────────────────────────────────────────

  markOfflineStale: async (staleMinutes = 5) => {
    const { data } = await api.post("/routers/mark-offline-stale", {
      stale_minutes: staleMinutes,
    });
    return data;
  },

  // ── Authentication readiness (Release D backend, Stage A UI) ───────────────
  // Whether this router can actually authenticate customers: an enabled tunnel
  // in the SERVING region plus a tenant-matched RADIUS client with a secret.
  // Reads stored state only — it never touches the device, so showing it on a
  // page costs no RouterOS work.
  //
  // The response never contains a secret; only `radius_secret_present`.
  getAuthReadiness: async (routerId, config = {}) => {
    const { data } = await api.get(`/routers/${routerId}/auth-readiness`, config);
    return data;
  },

  // Read this router's configuration and RECORD the verdict readiness is built
  // from. Part of Live check, so pressing it can clear a stale warning.
  verifyLive: async (routerId) => {
    const { data } = await api.post(`/routers/${routerId}/verify-live`, {}, LONG_RUNNING);
    return data;
  },
};

/* =========================
   PACKAGE SERVICES
========================= */
export const packageService = {
  // Admin list. The Packages manager passes { includeInactive: true } so inactive
  // packages remain visible and can be re-activated; other callers keep the
  // previous active-only behaviour.
  getPackages: async (routerId, { includeInactive = false } = {}) => {
    const { data } = await api.get(`/packages`, {
      params: { router_id: routerId, include_inactive: includeInactive ? "true" : "false" },
    });
    return data;
  },

  addPackage: async (packageData) => {
    const { data } = await api.post("/packages", packageData);
    return data;
  },

  updatePackage: async (id, packageData) => {
    const { data } = await api.put(`/packages/${id}`, packageData);
    return data;
  },

  deletePackage: async (id) => {
    const { data } = await api.delete(`/packages/${id}`);
    return data;
  },

  // Archive, not delete. A promotion that has been claimed carries the history
  // its report is built from, and a hard delete would sever the vouchers,
  // entitlements and sessions that point at it. Archiving hides it everywhere —
  // the portal, the promotions list, the default package list — writes only the
  // `packages` row, and disconnects nobody.
  //
  // The server answers 409 `confirmation_required` when the promotion still has
  // customers or unredeemed vouchers attached, and returns the counts with it,
  // so the caller can say what is attached before asking again with confirm.
  archivePackage: async (id, { confirm = false } = {}) => {
    const { data } = await api.post(`/packages/${id}/archive`, { confirm });
    return data;
  },
};

/* =========================
   VOUCHER SERVICES
========================= */
export const voucherService = {
  getVouchers: async (routerId = "", config = {}) => {
    const params = {};
    if (routerId) params.router_id = routerId;
    const { data } = await api.get("/vouchers", { ...config, params: { ...params, ...(config.params || {}) } });
    return data;
  },

  // What a batch delete WOULD do. Deletes nothing. Uses the same predicate as
  // the delete itself, so the warning cannot disagree with the outcome.
  getBatchDeletePreview: async (batchId) => {
    const { data } = await api.get(`/vouchers/batch/${encodeURIComponent(batchId)}/delete-preview`);
    return data;
  },

  getVoucherByCode: async (code) => {
    const { data } = await api.get(`/vouchers/${encodeURIComponent(code)}`);
    return data;
  },

  getBatch: async (batchId) => {
    const { data } = await api.get(`/vouchers/batch/${encodeURIComponent(batchId)}`);
    return data;
  },

  // Voucher Generation History — who generated which batch (attribution).
  getBatches: async (routerId = "", config = {}) => {
    const params = {};
    if (routerId) params.router_id = routerId;
    const { data } = await api.get("/vouchers/batches", { ...config, params });
    return data;
  },

  createVoucher: async (payload) => {
    const { data } = await api.post("/vouchers", payload);
    return data;
  },

  deleteVoucherById: async (id, payload = {}) => {
    const { data } = await api.post(`/vouchers/id/${id}/delete`, payload);
    return data;
  },

  deleteBatch: async (batchId, payload = {}) => {
    const { data } = await api.post(`/vouchers/batch/${encodeURIComponent(batchId)}/delete`, payload);
    return data;
  },

  // Extend the CODE expiry of a batch's unused vouchers to an exact instant.
  // A target rather than "+30 days", so a retried request cannot extend twice.
  extendBatchExpiry: async (batchId, newExpiry) => {
    const { data } = await api.post(`/vouchers/batch/${encodeURIComponent(batchId)}/extend-expiry`, { new_expiry: newExpiry });
    return data;
  },

  // [{ batch_id, new_expiry }] — each batch is extended in its own transaction.
  extendBatchesExpiry: async (batches) => {
    const { data } = await api.post("/vouchers/batches/extend-expiry", { batches });
    return data;
  },

  redeemVoucher: async (code, payload) => {
    const { data } = await api.post(`/vouchers/${encodeURIComponent(code)}/redeem`, payload);
    return data;
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 1. ADD THIS TO: frontend/src/services/api.js
//    Place it alongside voucherService, routerService, etc.
// ─────────────────────────────────────────────────────────────────────────────
export const tenantSettingsService = {
  // Voucher branding (logo_base64, business_name, footer_note, voucher_design).
  // With a routerId it is that router's own branding, or the owner's when the
  // router has none yet — `settings.scope` says which ("router" | "default").
  get: async (routerId) => {
    const params = routerId ? { router_id: routerId } : {};
    const response = await api.get("/tenant-settings", { params });
    return response.data;
  },

  // Update one or more branding fields.
  // Pass only the keys you want to change — unspecified keys are left as-is.
  // With a routerId only that router's vouchers change.
  update: async (payload, routerId) => {
    const body = routerId ? { ...payload, router_id: Number(routerId) } : payload;
    const response = await api.put("/tenant-settings", body);
    return response.data;
  },

  // Remove just the logo (leaves business_name and footer_note intact)
  deleteLogo: async (routerId) => {
    const params = routerId ? { router_id: routerId } : {};
    const response = await api.delete("/tenant-settings/logo", { params });
    return response.data;
  },
};

export const settingsRolesService = {
  // Catalog (permission groups + role templates) + team members.
  list: async () => {
    const { data } = await api.get("/settings/roles");
    return data;
  },

  // Create a staff user directly.
  // payload: { username, password, email?, phone?, role_template, permissions?, status, otp? }
  createUser: async (payload) => {
    const { data } = await api.post("/settings/roles", payload);
    return data;
  },

  // Edit a member. payload: { role_template?, permissions?, status?, username?, email?, phone?, password?, otp? }
  update: async (id, payload) => {
    const { data } = await api.put(`/settings/roles/${id}`, payload);
    return data;
  },

  resetPassword: async (id, payload) => {
    const { data } = await api.post(`/settings/roles/${id}/reset-password`, payload);
    return data;
  },

  remove: async (id, payload = {}) => {
    const { data } = await api.delete(`/settings/roles/${id}`, { data: payload });
    return data;
  },

  // Request an admin-confirmation OTP emailed to the tenant owner.
  requestAdminOtp: async () => {
    const { data } = await api.post("/settings/roles/request-admin-otp");
    return data;
  },

  activity: async (limit = 50) => {
    const { data } = await api.get("/settings/roles/activity", { params: { limit } });
    return data;
  },
};

/* =========================
   PERMISSIONS (current user)
========================= */
export const permissionsService = {
  // Effective permissions for the logged-in user (drives sidebar + guards).
  me: async (config = {}) => {
    const { data } = await api.get("/users/me/permissions", config);
    return data;
  },
};

// ── Billing / Roaming Service ─────────────────────────────────────────────────
// Roaming is a BUSINESS-WIDE setting; the server resolves the business from the
// selected router (trusted server state), so every call passes the router id.
export const billingService = {
  // Roaming state + business context for the selected router's business.
  getConfig: async (routerId, config = {}) => {
    const { data } = await api.get(`/billing/config?router_id=${routerId}`, config);
    return data;
  },
  // Prerequisite check before enabling (no secrets returned).
  getRoamingReadiness: async (routerId, config = {}) => {
    const { data } = await api.get(`/billing/roaming/readiness?router_id=${routerId}`, config);
    return data;
  },
  // Enable/disable roaming for the selected router's business.
  setRoaming: async (routerId, enabled) => {
    const { data } = await api.put("/billing/roaming", { router_id: routerId, enabled });
    return data;
  },
};

// ── Agent Allocation Service ──────────────────────────────────────────────────
export const agentAllocationService = {
  create: async (payload) => {
    const { data } = await api.post('/agent-allocations', payload);
    return data;
  },

  // One hand-over, one transaction, one invoice — however many plans it holds.
  // The caller supplies `idempotency_key`; the backend's unique index on it is
  // what makes a double-tapped Confirm return the FIRST invoice instead of
  // minting a second set of vouchers.
  createInvoice: async (payload) => {
    const { data } = await api.post('/agent-allocations/invoice', payload);
    return data;
  },

  list: async (params = {}) => {
    const { data } = await api.get('/agent-allocations', { params });
    return data;
  },

  // ONE invoice as it stands NOW — not the issuance response the browser kept.
  // The server reads paid straight from the payment-allocation rows, so an
  // invoice reopened a week later shows the balance the payment engine would
  // actually enforce today. See routes/agentAllocations.js GET /invoice/:id.
  getInvoice: async (invoiceId) => {
    const { data } = await api.get(`/agent-allocations/invoice/${invoiceId}`);
    return data;
  },

  getAgentVouchers: async (agentId, params = {}) => {
    const { data } = await api.get(`/agent-allocations/agent/${agentId}/vouchers`, { params });
    return data;
  },
};

// ── Agent Payment Service ─────────────────────────────────────────────────────
// Every screen that takes money from an agent goes through here, and every one
// of these lands in services/agentPayments.js on the server. There is no second
// payment path to drift from: the Agents drawer, the Collections shortcut and
// an invoice screen all post the same shape.
export const agentPaymentService = {
  // What a payment can be applied to: one row per package, oldest first, with
  // expected / paid / outstanding already resolved by the server.
  outstanding: async (agentId) => {
    const { data } = await api.get(`/agents/${agentId}/outstanding`);
    return data;
  },

  // `allocations` is the per-package split (§23): [{ agent_batch_id, amount }].
  // Omit it and the server fills outstanding packages oldest-first. The amounts
  // must add up to `amount` — the server refuses the request otherwise rather
  // than quietly keeping the difference.
  record: async (agentId, payload) => {
    const { data } = await api.post(`/agents/${agentId}/payments`, payload);
    return data;
  },

  // Permanent history. A payment is never overwritten by a later one, so this
  // only grows; each entry carries what it was applied to.
  history: async (agentId, params = {}) => {
    const { data } = await api.get(`/agents/${agentId}/payments`, { params });
    return data;
  },

  // Undo a payment that should not have been recorded. The row stays in
  // history marked as reversed; the balance goes back to what it was.
  reverse: async (agentId, paymentId, reason) => {
    const { data } = await api.post(
      `/agents/${agentId}/payments/${paymentId}/reverse`, { reason }
    );
    return data;
  },
};

/* =========================
   AGENT ACCOUNT SERVICES
   The agent's operational account: stock the owner still holds, allocating it,
   correcting an allocation, and what the business spent on an agent.
========================= */
export const agentAccountService = {
  // Vouchers that were minted and never given to anybody — grouped by the plan
  // and price they carry, because that is the pile the owner is holding.
  unassignedStock: async (params = {}) => {
    const { data } = await api.get("/agents/admin/unassigned-vouchers", { params });
    return data;
  },

  // Hand existing vouchers to an agent. Produces the SAME agent_batches +
  // voucher_allocations rows generate-for-agent produces, which is what makes
  // Collections, Record Payment and the reports find it afterwards.
  allocateExisting: async (agentId, payload) => {
    const { data } = await api.post(`/agents/${agentId}/allocate-existing`, payload);
    return data;
  },

  // Vouchers came back, were lost, or the allocation was wrong.
  // kind: "return" | "write_off" | "correction".
  adjustAllocation: async (agentId, batchId, payload) => {
    const { data } = await api.post(
      `/agents/${agentId}/allocations/${batchId}/adjust`, payload
    );
    return data;
  },

  // What the business spent because of this agent. Reads Financials' table —
  // there is no separate agent expense record.
  expenses: async (agentId, params = {}) => {
    const { data } = await api.get(`/agents/${agentId}/expenses`, { params });
    return data;
  },

  // Stop trading with an agent without erasing what they owe or paid.
  setStatus: async (agentId, status) => {
    const { data } = await api.patch(`/agents/${agentId}/status`, { status });
    return data;
  },
};

/* =========================
   FINANCIALS
   Money in and money out for the whole business. A reader over records other
   parts of PesaFii already own, plus one table of its own for the costs and
   owner movements nothing else records.
========================= */
export const financialsService = {
  // The taxonomy: which kinds exist, what they mean, which side they fall on.
  // Fetched rather than duplicated in the client so a change to what a kind
  // means reaches the dropdown and the report together.
  categories: async () => {
    const { data } = await api.get("/financials/categories");
    return data;
  },

  summary: async (params = {}) => {
    const { data } = await api.get("/financials/summary", { params });
    return data;
  },

  // §20/§21 — the whole Cashbook summary in ONE request: a few recent records
  // per group. Five requests from a phone is five handshakes before anything
  // renders; these are five small reads that run together on the server.
  cashbook: async (params = {}) => {
    const { data } = await api.get("/financials/cashbook", { params });
    return data;
  },

  // §19 — costs already recorded that no withdrawal has paid, for the
  // "this pays a cost I already recorded" step of a withdrawal.
  unsettledCosts: async (params = {}) => {
    const { data } = await api.get("/financials/unsettled-costs", { params });
    return data;
  },

  // Server-paginated. A year of wallet rows is never shipped to a phone.
  // `group` narrows to one part of the cashbook, so View history lands the
  // reader where they tapped.
  entries: async (params = {}) => {
    const { data } = await api.get("/financials/entries", { params });
    return data;
  },

  createEntry: async (payload) => {
    const { data } = await api.post("/financials/entries", payload);
    return data;
  },

  // ── the two registers that are NOT money (§8, §20) ───────────────────────
  //
  // Kept on financialsService because Financials is where an owner records one,
  // and named so nothing here reads like an amount. A business event's
  // `estimated_impact` is context for a human; it never reaches a total.
  registerOptions: async () => {
    const { data } = await api.get("/financials/register-options");
    return data;
  },
  events: async (params = {}) => {
    const { data } = await api.get("/financials/events", { params });
    return data;
  },
  createEvent: async (payload) => {
    const { data } = await api.post("/financials/events", payload);
    return data;
  },
  updateEvent: async (id, payload) => {
    const { data } = await api.put(`/financials/events/${id}`, payload);
    return data;
  },
  deleteEvent: async (id) => {
    const { data } = await api.delete(`/financials/events/${id}`);
    return data;
  },
  assets: async (params = {}) => {
    const { data } = await api.get("/financials/assets", { params });
    return data;
  },
  createAsset: async (payload) => {
    const { data } = await api.post("/financials/assets", payload);
    return data;
  },
  updateAsset: async (id, payload) => {
    const { data } = await api.put(`/financials/assets/${id}`, payload);
    return data;
  },
  deleteAsset: async (id) => {
    const { data } = await api.delete(`/financials/assets/${id}`);
    return data;
  },

  updateEntry: async (id, payload) => {
    const { data } = await api.put(`/financials/entries/${id}`, payload);
    return data;
  },

  // There is no delete. A financial record that can vanish is not a record.
  reverseEntry: async (id, reason) => {
    const { data } = await api.post(`/financials/entries/${id}/reverse`, { reason });
    return data;
  },
};

/* =========================
   REPORTS
   Read-only aggregates over the same records. No reporting tables.
========================= */
export const reportsService = {
  summary:           async (p = {}) => (await api.get("/reports/summary",           { params: p })).data,
  incomeExpense:     async (p = {}) => (await api.get("/reports/income-expense",    { params: p })).data,
  cashFlow:          async (p = {}) => (await api.get("/reports/cash-flow",         { params: p })).data,
  agents:            async (p = {}) => (await api.get("/reports/agents",            { params: p })).data,
  expenses:          async (p = {}) => (await api.get("/reports/expenses",          { params: p })).data,
  salesCollections:  async (p = {}) => (await api.get("/reports/sales-collections", { params: p })).data,
  // §35 — what the business spent on expanding, and how it was funded.
  businessGrowth:    async (p = {}) => (await api.get("/reports/business-growth",   { params: p })).data,
};

/* =========================
   VOUCHER TEMPLATE SERVICES
========================= */
export const voucherTemplateService = {
  // With a routerId: that router's plans plus plans shared by all routers.
  // Without one: every plan the owner has.
  list: async (routerId) => {
    const params = routerId ? { router_id: routerId } : {};
    const { data } = await api.get("/voucher-templates", { params });
    return data;
  },

  get: async (id) => {
    const { data } = await api.get(`/voucher-templates/${id}`);
    return data;
  },

  create: async (payload) => {
    const { data } = await api.post("/voucher-templates", payload);
    return data;
  },

  update: async (id, payload) => {
    const { data } = await api.put(`/voucher-templates/${id}`, payload);
    return data;
  },

  remove: async (id) => {
    const { data } = await api.delete(`/voucher-templates/${id}`);
    return data;
  },

  // Live "Apply to all" propagation status (RADIUS CoA to active sessions).
  propagationStatus: async (jobId) => {
    const { data } = await api.get(`/voucher-templates/propagation/${jobId}`);
    return data;
  },

  // Explicit "Apply to all": force the plan's current speed onto every linked
  // voucher + RADIUS + active session (repair, no diff required).
  applyToAll: async (id) => {
    const { data } = await api.post(`/voucher-templates/${id}/apply-to-all`, {});
    return data;
  },
};

export const portalService = {
  authorizeVoucher: async (payload) => {
    const { data } = await api.post("/portal/authorize-voucher", payload);
    return data;
  },

  // Activate a FREE promotional package (no payment). Backend enforces that the
  // package is an active promotion within its window — paid packages are refused.
  freeConnect: async (payload) => {
    const { data } = await api.post("/portal/package/free-connect", payload);
    return data;
  },
};

/* =========================
   DASHBOARD SERVICES (Phase 1 — Hotspot Command Center)
========================= */
export const dashboardService = {
  // Legacy consolidated summary (unchanged backend contract).
  getSummary: async (params = {}, config = {}) => {
    const { data } = await api.get("/dashboard/summary", { ...config, params });
    return data;
  },

  // Phase 1 command-center payload (revenue split, network, users,
  // peak hours, last buyers, recommendations). Tenant-scoped on the backend.
  // When routerId is provided the whole payload is scoped to that router
  // (per-router "tabs" behaviour). nocache=1 forces a fresh status check.
  getCommandCenter: async (params = {}, config = {}) => {
    // Accept either spelling from callers, but ALWAYS send snake_case router_id
    // on the wire (that is what the backend route reads).
    const routerId = params.router_id ?? params.routerId;
    const query = {
      from: params.from,
      to: params.to,
      router_id: routerId,
      nocache: params.nocache,
      fresh: params.fresh,
      // Progressive loading. "core" returns revenue/wallet/live users without
      // the capacity-history work that dominates a cold rebuild (~2,007 ms of
      // ~2,955 ms). Omitted => the full payload, exactly as before.
      sections: params.sections,
    };
    // Drop undefined / null / "" so we never send an empty scope by accident.
    Object.keys(query).forEach((k) => {
      if (query[k] === undefined || query[k] === null || query[k] === "") delete query[k];
    });
    // ── ONE REQUEST PER SCOPE ────────────────────────────────────────────────
    // Three callers now want the same payload: the shell's idle prefetch, the
    // Dashboard's own mount, and a return navigation. Without this they issue
    // three identical GETs — the prefetch would ADD traffic rather than move it
    // earlier, which is the opposite of the point.
    //
    // A short result cache handles the common case (prefetch lands, user opens
    // the Dashboard a second later); in-flight joining handles the race (user
    // taps Dashboard while the prefetch is still open).
    //
    // `nocache` NEVER participates. An operator pressing Refresh is asking the
    // server for a current answer, and handing them a cached one — or letting
    // them join a request that did not ask for a rebuild — would silently ignore
    // what they asked for.
    const wantsFresh = Boolean(query.nocache || query.fresh);
    const scope = JSON.stringify([
      query.router_id ?? "", query.from ?? "", query.to ?? "", query.sections ?? "",
    ]);

    if (!wantsFresh) {
      const hit = dashboardService._ccCache.get(scope);
      if (hit && isCurrentEpoch(hit.epoch) && Date.now() - hit.at < dashboardService._ccTtlMs) {
        return hit.data;
      }
      // A caller with its own AbortSignal is never shared: its abort would
      // cancel everyone else's request. It may still POPULATE the cache below.
      if (!config.signal) {
        const flight = dashboardService._ccInflight.get(scope);
        if (flight && isCurrentEpoch(flight.epoch)) return flight.promise;
      }
    }

    const epoch = getAuthEpoch();
    const promise = api
      .get("/dashboard/hotspot-command-center", { ...config, params: query })
      .then(({ data }) => {
        // Belongs to a previous account — refuse it rather than hand it over.
        if (!isCurrentEpoch(epoch)) {
          const e = new Error("Dashboard payload belongs to a previous session");
          e.code = "STALE_ACCOUNT";
          throw e;
        }
        if (data?.success) dashboardService._ccCache.set(scope, { at: Date.now(), epoch, data });
        return data;
      })
      .finally(() => {
        const cur = dashboardService._ccInflight.get(scope);
        if (cur && cur.promise === promise) dashboardService._ccInflight.delete(scope);
      });

    if (!wantsFresh && !config.signal) {
      dashboardService._ccInflight.set(scope, { epoch, promise });
    }
    return promise;
  },

  // Scope -> { at, epoch, data }. Seconds, not a session-long store: this exists
  // to collapse a prefetch and a mount that happen moments apart, not to become
  // a second cache with its own staleness rules. The server's own
  // stale-while-revalidate is what keeps the answer current.
  _ccCache: new Map(),
  _ccInflight: new Map(),
  _ccTtlMs: 10000,

  /** Drop everything cached here — used by the shared account teardown. */
  clearCommandCenterCache() {
    dashboardService._ccCache.clear();
    dashboardService._ccInflight.clear();
  },

  // Peak Hours card data — its OWN endpoint with a LOCAL period selector
  // (today | yesterday | 7d | 30d). Deliberately independent of the global
  // financial from/to range: the backend derives the date boundaries from the
  // period name. Router scope stays the globally selected (ownership-checked)
  // router.
  getPeakHours: async (params = {}, config = {}) => {
    const routerId = params.router_id ?? params.routerId;
    const query = {
      router_id: routerId,
      period: params.period,
      nocache: params.nocache,
    };
    Object.keys(query).forEach((k) => {
      if (query[k] === undefined || query[k] === null || query[k] === "") delete query[k];
    });
    const { data } = await api.get("/dashboard/peak-hours", { ...config, params: query });
    return data;
  },

  // Peak Activity card data — a REAL time series on its own endpoint.
  //
  // One request serves all three metric tabs (Active Users, Bandwidth, Sales),
  // because the range is shared and two of the three come from the same sample
  // rows. Switching tabs therefore triggers no request at all.
  //
  //   range  "12h" (rolling, the default) | "day" | "week"
  //   day    'YYYY-MM-DD' anchor for day/week; the server clamps a future date
  //          to today and derives all boundaries in the business timezone, so
  //          the client can never smuggle an arbitrary window in.
  getPeakActivity: async (params = {}, config = {}) => {
    const routerId = params.router_id ?? params.routerId;
    const query = {
      router_id: routerId,
      range: params.range,
      day: params.day,
      nocache: params.nocache,
    };
    Object.keys(query).forEach((k) => {
      if (query[k] === undefined || query[k] === null || query[k] === "") delete query[k];
    });
    const { data } = await api.get("/dashboard/peak-activity", { ...config, params: query });
    return data;
  },

  // Scrollable "Last N Buyers" feed (most recent package buyers).
  getLastBuyers: async (limit = 100, config = {}) => {
    const { data } = await api.get("/dashboard/last-buyers", {
      ...config,
      params: { limit },
    });
    return data;
  },
};

/* =========================
   HEALTH CHECK
========================= */
export const healthService = {
  checkHealth: async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/health`);
      return data;
    } catch {
      return { server: "unhealthy", database: "disconnected" };
    }
  },
};

// ── Remote WinBox Access (temporary VPS relay) ────────────────────────────────
// Kept as its own service (not folded into routerService) since it is a separate
// feature module with its own backend routes under /api/remote-access.
export const remoteAccessService = {
  listSessions: async (routerId = "") => {
    const params = routerId ? { router_id: routerId } : {};
    const { data } = await api.get("/remote-access/sessions", { params });
    return data;
  },
  openSession: async (routerId, ttlMinutes) => {
    const body = { router_id: routerId };
    if (ttlMinutes) body.ttl_minutes = ttlMinutes;
    const { data } = await api.post("/remote-access/sessions", body);
    return data;
  },
  getSession: async (id) => {
    const { data } = await api.get(`/remote-access/sessions/${id}`);
    return data;
  },
  closeSession: async (id) => {
    const { data } = await api.post(`/remote-access/sessions/${id}/close`);
    return data;
  },
  refreshSession: async (id, ttlMinutes) => {
    const body = ttlMinutes ? { ttl_minutes: ttlMinutes } : {};
    const { data } = await api.post(`/remote-access/sessions/${id}/refresh`, body);
    return data;
  },
};

// ── Customer migration (Migrate Existing Customers) ──────────────────────────
// Multipart uploads: axios sets the multipart boundary itself when handed a
// FormData body; the per-call header override never leaks into other calls.
// Structured source-connection outcomes (management relay detected,
// authentication failed, timeout, ...) are EXPECTED migration states, not
// application crashes. The backend deliberately answers them with non-2xx
// statuses, so axios REJECTS and the structured body lives on
// error.response.data — one missed catch away from being swallowed silently.
// This is the single normalization point (§4): when the rejection carries a
// migration classification, the service RESOLVES with that structured object
// ({ success:false, code, classification, ... }) so consumers key off stable
// fields on one code path. Anything without a structured body (network/CORS/
// proxy failure) still rejects and must be shown as a generic, visible error —
// never silence, never raw axios internals.
function normalizeSourceConnectionError(error) {
  const payload = error?.response?.data ?? error?.data ?? null;
  if (payload && typeof payload === "object" && (payload.code || payload.classification)) {
    return { success: false, ...payload };
  }
  return null;
}

export const customerMigrationService = {
  preview: async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/customer-migration/preview", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  createBatch: async (file, fields) => {
    const form = new FormData();
    form.append("file", file);
    Object.entries(fields || {}).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      form.append(k, typeof v === "object" ? JSON.stringify(v) : String(v));
    });
    const { data } = await api.post("/customer-migration/batches", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  listBatches: async (routerId) => {
    const params = routerId ? { router_id: routerId } : {};
    const { data } = await api.get("/customer-migration/batches", { params });
    return data;
  },
  getBatch: async (id) => {
    const { data } = await api.get(`/customer-migration/batches/${id}`);
    return data;
  },
  getRecords: async (id, params = {}) => {
    const { data } = await api.get(`/customer-migration/batches/${id}/records`, { params });
    return data;
  },
  updateRecord: async (batchId, recordId, body) => {
    const { data } = await api.patch(`/customer-migration/batches/${batchId}/records/${recordId}`, body);
    return data;
  },
  importBatch: async (id) => {
    const { data } = await api.post(`/customer-migration/batches/${id}/import`, {}, LONG_RUNNING);
    return data;
  },
  readiness: async (id) => {
    const { data } = await api.get(`/customer-migration/batches/${id}/readiness`);
    return data;
  },
  activateBatch: async (id) => {
    const { data } = await api.post(`/customer-migration/batches/${id}/activate`);
    return data;
  },
  cancelBatch: async (id) => {
    const { data } = await api.post(`/customer-migration/batches/${id}/cancel`);
    return data;
  },
  rebindRecord: async (batchId, recordId, macAddress) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/records/${recordId}/rebind`, { mac_address: macAddress });
    return data;
  },
  // ── Direct MikroTik Migration Mode ──────────────────────────────────────────
  getConfig: async () => {
    const { data } = await api.get("/customer-migration/config");
    return data;
  },
  testSource: async (payload) => {
    try {
      const { data } = await api.post("/customer-migration/source/test", payload);
      return data;
    } catch (error) {
      const structured = normalizeSourceConnectionError(error);
      if (structured) return structured;
      throw error;
    }
  },
  discoverSource: async (payload) => {
    try {
      const { data } = await api.post("/customer-migration/source/discover", payload);
      return data;
    } catch (error) {
      const structured = normalizeSourceConnectionError(error);
      if (structured) return structured;
      throw error;
    }
  },
  startMigrationMode: async (batchId, intervalSeconds) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/migration-mode/start`, { sync_interval_seconds: intervalSeconds });
    return data;
  },
  stopMigrationMode: async (batchId) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/migration-mode/stop`);
    return data;
  },
  syncStatus: async (batchId) => {
    const { data } = await api.get(`/customer-migration/batches/${batchId}/sync-status`);
    return data;
  },
  refreshSync: async (batchId) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/sync/refresh`);
    return data;
  },
  finalSync: async (batchId) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/sync/final`);
    return data;
  },
  setRecovery: async (batchId, body) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/recovery`, body);
    return data;
  },
  finishMigration: async (batchId) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/finish`);
    return data;
  },
  // Rehearsal / pilot cutover — proves the destination flow on explicitly
  // selected test identities WITHOUT finishing the production migration.
  getRehearsal: async (batchId) => {
    const { data } = await api.get(`/customer-migration/batches/${batchId}/rehearsal`);
    return data;
  },
  rehearsalCandidates: async (batchId) => {
    const { data } = await api.get(`/customer-migration/batches/${batchId}/rehearsal/candidates`);
    return data;
  },
  startRehearsal: async (batchId, identities) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/rehearsal`, { identities });
    return data;
  },
  endRehearsal: async (batchId) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/rehearsal/end`);
    return data;
  },
  // Operator's explicit, audited choice for unresolved-entitlement customers
  // at cutover (required by the backend finish gate when any remain).
  setUnresolvedPolicy: async (batchId, policy) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/unresolved-policy`, { policy });
    return data;
  },
  // Authoritative billing-export enrichment of the EXISTING mirror (never
  // creates a second customer population).
  enrichBatch: async (batchId, file, { mapping, snapshotAt } = {}) => {
    const form = new FormData();
    form.append("file", file);
    if (mapping) form.append("mapping", JSON.stringify(mapping));
    if (snapshotAt) form.append("snapshot_at", snapshotAt);
    const { data } = await api.post(`/customer-migration/batches/${batchId}/enrich`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  rollbackReport: async (batchId) => {
    const { data } = await api.get(`/customer-migration/batches/${batchId}/rollback-report`);
    return data;
  },
  disconnectSource: async (batchId) => {
    const { data } = await api.post(`/customer-migration/batches/${batchId}/source/disconnect`);
    return data;
  },
  // ── Temporary PesaFii migration access (WinBox Terminal fallback) ───────────
  createAccess: async (routerId) => {
    const { data } = await api.post("/customer-migration/source/access", { router_id: routerId });
    return data;
  },
  getAccess: async (id) => {
    const { data } = await api.get(`/customer-migration/source/access/${id}`);
    return data;
  },
  verifyAccess: async (id) => {
    const { data } = await api.post(`/customer-migration/source/access/${id}/verify`);
    return data;
  },
  // Standalone Advanced Recovery: no current access object needed. The access
  // is resolved server-side by tenant + migration code and checked against the
  // selected destination router. Generates nothing.
  recoverAccessByCode: async (routerId, migrationCode, publicKey) => {
    const { data } = await api.post("/customer-migration/source/access/recover", {
      router_id: routerId,
      migration_code: migrationCode,
      public_key: publicKey,
    });
    return data;
  },
  recoverAccess: async (id, publicKey, migrationCode) => {
    const { data } = await api.post(`/customer-migration/source/access/${id}/recover`, {
      public_key: publicKey,
      migration_code: migrationCode,
    });
    return data;
  },
  getAccessCleanup: async (id) => {
    const { data } = await api.get(`/customer-migration/source/access/${id}/cleanup`);
    return data;
  },
  removeAccess: async (id) => {
    const { data } = await api.post(`/customer-migration/source/access/${id}/remove`);
    return data;
  },
};

// Sites — the premises a router sits at. One business can have several, and a
// site can hold several routers. `site_id` on a router is nullable, so a tenant
// with one router never has to see the concept.
export const siteService = {
  list: async () => {
    const { data } = await api.get("/sites");
    return data;
  },
  create: async (payload) => {
    const { data } = await api.post("/sites", payload);
    return data;
  },
  update: async (id, payload) => {
    const { data } = await api.put(`/sites/${id}`, payload);
    return data;
  },
  // Deleting a site UNFILES its routers (ON DELETE SET NULL). It can never
  // delete a router, which is why this is not a destructive action.
  remove: async (id) => {
    const { data } = await api.delete(`/sites/${id}`);
    return data;
  },
  // Pass siteId = null to unfile a router without deleting the site.
  assignRouter: async (routerId, siteId) => {
    const { data } = await api.put(`/sites/routers/${routerId}`, { site_id: siteId });
    return data;
  },
};

// Business Overview — the whole business on one page. Served from one cached
// payload with an honest `last_updated`; it never reads a router, so opening it
// costs the gateway nothing however many people are watching.
// ── Analytics (business-wide) ────────────────────────────────────────────────
// The tenant-wide form of the analytics endpoints the Analytics panel already
// uses. Business Overview reads only the Overview series from here, so its
// Revenue Trend plots the SAME per-day net revenue as every other net figure in
// the product rather than a second definition of the word.
export const analyticsService = {
  /**
   * Business-wide overview analytics for a date range.
   *
   * The path without a router id is the tenant-wide one, and that is the only
   * form this helper offers: Business Overview must never scope itself to the
   * selected router.
   *
   * Returns `overview_analytics` ({ kpis, series }) or null.
   */
  overview: async ({ from, to } = {}) => {
    const { data } = await api.get("/routers/analytics/overview", { params: { from, to } });
    if (!data?.success) throw new Error(data?.error || "Overview analytics unavailable");
    return data.overview_analytics || null;
  },
};

export const businessService = {
  /**
   * `from`/`to` scope the two revenue breakdowns to the toolbar's range. They
   * are deliberately the ONLY scope this call accepts: no router_id is sent,
   * and none may be added. Business Overview aggregates every router the tenant
   * owns, and passing the sidebar's selected router here would quietly turn the
   * business page into a router page.
   */
  overview: async ({ fresh = false, from, to } = {}) => {
    const params = {};
    if (fresh) params.fresh = 1;
    if (from) params.from = from;
    if (to) params.to = to;
    const { data } = await api.get("/business/overview", { params });
    return data;
  },
};

// ── Network Service ───────────────────────────────────────────────────────────
// The shape of the owner's network: Business → Site → Router, served from stored
// state only. One cached payload, no RouterOS read — see
// backend/services/networkTopology.js.
export const networkService = {
  topology: async ({ fresh = false } = {}) => {
    const { data } = await api.get(`/network/topology${fresh ? "?fresh=1" : ""}`);
    return data;
  },
};

/* =========================
   PROMOTIONS & LOYALTY REWARDS
   =========================
   A promotion IS a package (`package_type = 'promotion'`), so every promotion
   WRITE deliberately goes through packageService and /api/packages — the one
   place that owns package lifecycle, validation, audit and cache invalidation.
   Two write paths against the same table would be two sets of those rules, and
   the first time they disagreed a promotion would be editable in one place and
   not the other.

   What lives here is the read that lets the Promotions page open in ONE request,
   and the loyalty programme, which is genuinely new. */
export const promotionsService = {
  // Promotions + loyalty programme + summary + customer table, for one router.
  overview: async (routerId) => {
    const { data } = await api.get("/promotions", { params: { router_id: routerId } });
    return data;
  },
  // The detail behind "42 connected". Fetched only when the owner opens a
  // report — the page load already carries the number itself.
  report: async (routerId, packageId) => {
    const { data } = await api.get("/promotions/report", {
      params: { router_id: routerId, package_id: packageId },
    });
    return data;
  },
  loyalty: async (routerId, { limit } = {}) => {
    const { data } = await api.get("/promotions/loyalty", {
      params: { router_id: routerId, ...(limit ? { limit } : {}) },
    });
    return data;
  },
  // Save & Activate is one action for the owner and one request here.
  //
  // THE PAYLOAD IS FORWARDED, NOT RE-LISTED. It used to be destructured into
  // named fields and rebuilt, and when `announcement_theme` was added to the
  // form, to the API, to the database and to the captive portal, this one line
  // was not updated — so every theme an owner chose was dropped in the browser
  // and the server, seeing no field, kept the previous value. Five saves in the
  // production audit log record `gold -> gold` for exactly that reason, three of
  // them with the before and after identical in every field.
  //
  // The server validates every field against an allow-list and ignores anything
  // it does not know, so forwarding is not "trusting the client" — it is
  // removing a second, silent allow-list that nobody remembers to maintain.
  saveLoyalty: async (routerId, payload = {}) => {
    const { data } = await api.put("/promotions/loyalty", { ...payload, router_id: routerId });
    return data;
  },

  // ── Advanced Loyalty rules ────────────────────────────────────────────────
  // Payload FORWARDED, for the reason above: a re-listed allow-list here is the
  // exact shape of the announcement_theme bug, and these payloads carry ten
  // fields rather than three.
  loyaltyRules: async (routerId) => {
    const { data } = await api.get("/promotions/loyalty/rules", { params: { router_id: routerId } });
    return data;
  },
  createLoyaltyRule: async (routerId, payload = {}) => {
    const { data } = await api.post("/promotions/loyalty/rules", { ...payload, router_id: routerId });
    return data;
  },
  updateLoyaltyRule: async (routerId, id, payload = {}) => {
    const { data } = await api.put(`/promotions/loyalty/rules/${id}`, { ...payload, router_id: routerId });
    return data;
  },
  deleteLoyaltyRule: async (routerId, id) => {
    const { data } = await api.delete(`/promotions/loyalty/rules/${id}`, { params: { router_id: routerId } });
    return data;
  },

  // ── Announcements & ads ───────────────────────────────────────────────────
  announcements: async (routerId) => {
    const { data } = await api.get("/promotions/announcements", { params: { router_id: routerId } });
    return data;
  },
  createAnnouncement: async (routerId, payload = {}) => {
    const { data } = await api.post("/promotions/announcements", { ...payload, router_id: routerId });
    return data;
  },
  updateAnnouncement: async (routerId, id, payload = {}) => {
    const { data } = await api.put(`/promotions/announcements/${id}`, { ...payload, router_id: routerId });
    return data;
  },
  deleteAnnouncement: async (routerId, id) => {
    const { data } = await api.delete(`/promotions/announcements/${id}`, { params: { router_id: routerId } });
    return data;
  },
};


/* =========================
   PESAFII PARTNERS
   "Refer networks. Earn as they grow."

   Partner is PERSON-scoped, not tenant-scoped: the server resolves the
   authenticated login to a Partner and never accepts a partner_id from here.
   So none of these calls carries one, and none of them takes a tenant.
========================= */
export const partnerService = {
  // Does this login have a Partner profile? The only call that must NOT 403 for
  // someone who has not joined — the Join page asks it to decide what to show.
  me:        async ()         => (await api.get("/partners/me")).data,
  join:      async ()         => (await api.post("/partners/join")).data,

  overview:  async ()         => (await api.get("/partners/overview")).data,
  clients:   async (p = {})   => (await api.get("/partners/clients", { params: p })).data,
  client:    async (tenantId) => (await api.get(`/partners/clients/${tenantId}`)).data,
  // Server-paginated, always. One busy referred client accrues a commission row
  // per qualifying payment — the busiest tenant in production takes ~114 a day.
  earnings:  async (p = {})   => (await api.get("/partners/earnings", { params: p })).data,
  earning:   async (id)       => (await api.get(`/partners/earnings/${id}`)).data,
  program:   async ()         => (await api.get("/partners/program")).data,

  // The public /partners marketing page. No authentication, and deliberately a
  // separate endpoint from program() above: this one carries no referral code
  // and no readiness detail, so it is safe on a page a stranger can open. It
  // reads the same policy the commission engine prices against, which is what
  // stops the public page advertising a rate the engine no longer uses.
  publicProgram: async ()     => (await api.get("/partners/program/public")).data,

  // ── Payouts ──────────────────────────────────────────────────────────────
  // The same shape as the tenant Send Money flow, against the Partner Wallet.
  payoutQuote:  async (payload) => (await api.post("/partners/payouts/quote", payload)).data,
  payout:       async (payload) => (await api.post("/partners/payouts", payload)).data,
  payouts:      async (p = {})  => (await api.get("/partners/payouts", { params: p })).data,
  payoutStatus: async (id)      => (await api.get(`/partners/payouts/${id}`)).data,
};

/* =========================
   PUBLIC REFERRAL LOOKUP
   No authentication. Rate-limited server-side. Returns a display name and an
   opaque, purpose-bound ticket — never an internal partner id.
========================= */
export const referralService = {
  lookup: async (code) => {
    const { data } = await api.get(`/partners/r/${encodeURIComponent(code)}`);
    return data;
  },
};

export default api;
